/**
 * Types for the platform-api `/internal/attachments` endpoints
 * (epic #1031, #1060 — finance attachments infrastructure).
 *
 * Three endpoints share this module:
 *   POST /internal/attachments                       upload (base64 JSON)
 *   GET  /internal/attachments?entity_type=&entity_id=  list for an entity
 *   GET  /internal/attachments/:id                   download (base64 JSON)
 *
 * Objects live in the platform-owned OSS bucket
 * `router-finance-attachments-<env>` (long retention, deliberately no
 * cleanup cron). The `finance_attachments` metadata row is written only
 * after the OSS put succeeds, so a row always points at a stored object.
 *
 * `entity_type` is designed for reuse: receipts today, sales contracts and
 * Epic 4 credit contracts later — extending the union below is the only
 * contract change those flows need.
 */

export const FINANCE_ATTACHMENT_ENTITY_TYPES = [
  'receipt_order',
  // Epic #1033 E4-C1 (#1125): mandatory credit-line contract, uploaded
  // (entity_id = customer org id) before a credit application references
  // its object_key.
  'credit_contract',
  // router#2000: optional payment-evidence attachment on a temporary
  // credit application (entity_id = customer org id). Text-only
  // evidence is also accepted; this type is the file form.
  'temporary_credit_evidence',
] as const;

export type FinanceAttachmentEntityType =
  (typeof FINANCE_ATTACHMENT_ENTITY_TYPES)[number];

export function isFinanceAttachmentEntityType(
  value: unknown,
): value is FinanceAttachmentEntityType {
  return (
    typeof value === 'string' &&
    (FINANCE_ATTACHMENT_ENTITY_TYPES as readonly string[]).includes(value)
  );
}

/**
 * Decoded-payload cap for the base64 JSON upload path (~5 MB, epic #1031
 * assumption). A binary passthrough route is deliberately deferred until
 * finance actually hits this cap.
 */
export const FINANCE_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;

export interface FinanceAttachmentSummary {
  id: string;
  entity_type: FinanceAttachmentEntityType;
  entity_id: string;
  /** Bucket-internal object key; never a public URL (no presigned URLs in v1). */
  object_key: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  /** Session-stamped by ops-api via `X-Ops-User-Id`; never client-supplied. */
  uploaded_by_ops_user_id: string | null;
  created_at: string;
}

export interface UploadFinanceAttachmentRequest {
  entity_type: FinanceAttachmentEntityType;
  entity_id: string;
  file_name: string;
  content_type: string;
  /** Standard base64 (`A-Za-z0-9+/` with `=` padding); decoded size ≤ {@link FINANCE_ATTACHMENT_MAX_BYTES}. */
  data_base64: string;
}

export interface UploadFinanceAttachmentResponse {
  attachment: FinanceAttachmentSummary;
}

export interface ListFinanceAttachmentsResponse {
  attachments: FinanceAttachmentSummary[];
}

export interface DownloadFinanceAttachmentResponse {
  attachment: FinanceAttachmentSummary;
  data_base64: string;
}

/**
 * The one refusal every finance-attachment read must be indistinguishable
 * from (#1957).
 *
 * Two different facts are deliberately reported with the same bytes:
 * platform-api answers this when no row has that id, and each ops-api read
 * surface answers it when the row exists but belongs to a different entity.
 * Fold those together and a caller holding an id learns nothing; let them
 * drift apart by one word and the pair becomes a clean existence oracle for
 * attachments the caller may not read.
 *
 * The indistinguishability is therefore a property of THREE call sites
 * agreeing, which is exactly what a hand-copied string cannot guarantee —
 * each site's own test would keep passing while the pair diverged. Every
 * producer, and every test stub that stands in for a producer, reads this.
 */
export const FINANCE_ATTACHMENT_NOT_FOUND: {
  readonly code: 'ATTACHMENT_NOT_FOUND';
  readonly message: string;
} = { code: 'ATTACHMENT_NOT_FOUND', message: 'finance attachment not found' };
